import React, { useState, useMemo } from 'react';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import Card from '../UI/Card';
import Modal from '../UI/Modal';
import ConfirmacaoModal from '../Common/ConfirmacaoModal';
import PrimaryButton from '../Common/PrimaryButton';
import DropdownMenu from '../UI/DropdownMenu';
import { useHistoricoMedico } from '../../hooks/data/useHistoricoMedico';
import { useNotification } from '../../context/NotificationContext';
import {
    createHistoricoMedico,
    updateHistoricoMedico,
    deleteHistoricoMedico,
    createAlergia,
    updateAlergia,
    deleteAlergia,
    createVacina,
    updateVacina,
    deleteVacina,
    createExame,
    updateExame,
    deleteExame,
    createHistoricoFamiliar,
    updateHistoricoFamiliar,
    deleteHistoricoFamiliar
} from '../../api/api';
import HistoricoMedicoForm from './Forms/HistoricoMedicoForm';
import AlergiaForm from './Forms/AlergiaForm';
import VacinaForm from './Forms/VacinaForm';
import ExameForm from './Forms/ExameForm';
import HistoricoFamiliarForm from './Forms/HistoricoFamiliarForm';
import HistoricoMedicoDocumentos from './HistoricoMedicoDocumentos';
import { 
    Heart, 
    Activity, 
    Hospital, 
    AlertTriangle, 
    Syringe, 
    FileText, 
    Users,
    Plus,
    Edit,
    Trash2,
    MoreVertical
} from 'lucide-react';

const PacienteHistoricoMedicoTab = ({ pacienteId }) => {
    const { error: showError } = useNotification();
    const [activeSection, setActiveSection] = useState('doencas');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleteType, setDeleteType] = useState(null);
    const [modalType, setModalType] = useState(null); // Tipo do modal: 'doenca', 'cirurgia', 'hospitalizacao', 'alergia', 'vacina', 'exame', 'historico-familiar'
    const [successMessage, setSuccessMessage] = useState(null);
    
    const { data, isLoading, error, refetch } = useHistoricoMedico(pacienteId);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric'
        });
    };

    // Função auxiliar para criar itens do dropdown de ações
    const createActionDropdownItems = (item, tipo, deleteType) => [
        {
            id: 'edit',
            label: 'Editar',
            icon: Edit,
            iconColor: 'text-blue-600',
            onClick: () => handleOpenModal(tipo, item)
        },
        {
            id: 'delete',
            label: 'Excluir',
            icon: Trash2,
            iconColor: 'text-red-600',
            onClick: () => handleDelete(item, deleteType)
        }
    ];

    // Handlers para histórico médico
    const handleOpenModal = (tipo, item = null) => {
        setEditingItem(item);
        setModalType(tipo);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setModalType(null);
        setSuccessMessage(null);
    };

    const handleSaveHistoricoMedico = async (formData) => {
        try {
            if (editingItem) {
                await updateHistoricoMedico(pacienteId, editingItem.id, formData);
                setSuccessMessage('Registro atualizado com sucesso!');
            } else {
                await createHistoricoMedico(pacienteId, formData);
                setSuccessMessage('Registro criado com sucesso!');
            }
            await refetch();
            setTimeout(() => handleCloseModal(), 1000);
        } catch (error) {
            throw error;
        }
    };

    const handleSaveAlergia = async (formData) => {
        try {
            if (editingItem) {
                await updateAlergia(pacienteId, editingItem.id, formData);
                setSuccessMessage('Alergia atualizada com sucesso!');
            } else {
                await createAlergia(pacienteId, formData);
                setSuccessMessage('Alergia registrada com sucesso!');
            }
            await refetch();
            setTimeout(() => handleCloseModal(), 1000);
        } catch (error) {
            throw error;
        }
    };

    const handleSaveVacina = async (formData) => {
        try {
            if (editingItem) {
                await updateVacina(pacienteId, editingItem.id, formData);
                setSuccessMessage('Vacina atualizada com sucesso!');
            } else {
                await createVacina(pacienteId, formData);
                setSuccessMessage('Vacina registrada com sucesso!');
            }
            await refetch();
            setTimeout(() => handleCloseModal(), 1000);
        } catch (error) {
            throw error;
        }
    };

    const handleSaveExame = async (formData) => {
        try {
            if (editingItem) {
                await updateExame(pacienteId, editingItem.id, formData);
                setSuccessMessage('Exame atualizado com sucesso!');
            } else {
                await createExame(pacienteId, formData);
                setSuccessMessage('Exame registrado com sucesso!');
            }
            await refetch();
            setTimeout(() => handleCloseModal(), 1000);
        } catch (error) {
            throw error;
        }
    };

    const handleSaveHistoricoFamiliar = async (formData) => {
        try {
            if (editingItem) {
                await updateHistoricoFamiliar(pacienteId, editingItem.id, formData);
                setSuccessMessage('Histórico familiar atualizado com sucesso!');
            } else {
                await createHistoricoFamiliar(pacienteId, formData);
                setSuccessMessage('Histórico familiar registrado com sucesso!');
            }
            await refetch();
            setTimeout(() => handleCloseModal(), 1000);
        } catch (error) {
            throw error;
        }
    };

    const handleDelete = (item, type) => {
        setDeletingItem(item);
        setDeleteType(type);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingItem || !deleteType) return;

        try {
            switch (deleteType) {
                case 'historico':
                    await deleteHistoricoMedico(pacienteId, deletingItem.id);
                    break;
                case 'alergia':
                    await deleteAlergia(pacienteId, deletingItem.id);
                    break;
                case 'vacina':
                    await deleteVacina(pacienteId, deletingItem.id);
                    break;
                case 'exame':
                    await deleteExame(pacienteId, deletingItem.id);
                    break;
                case 'historico-familiar':
                    await deleteHistoricoFamiliar(pacienteId, deletingItem.id);
                    break;
            }
            await refetch();
            setIsDeleteModalOpen(false);
            setDeletingItem(null);
            setDeleteType(null);
            setSuccessMessage('Registro removido com sucesso!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            console.error('Erro ao deletar:', error);
            showError(error?.response?.data?.message || 'Erro ao remover registro.');
        }
    };

    // Filtrar dados por tipo
    const doencas = useMemo(() => {
        return data?.historico?.filter(h => h.tipo === 'doenca') || [];
    }, [data?.historico]);
    
    const cirurgias = useMemo(() => {
        return data?.historico?.filter(h => h.tipo === 'cirurgia') || [];
    }, [data?.historico]);
    
    const hospitalizacoes = useMemo(() => {
        return data?.historico?.filter(h => h.tipo === 'hospitalizacao') || [];
    }, [data?.historico]);

    const sections = [
        { 
            id: 'doencas', 
            label: 'Doenças Prévia', 
            icon: Heart,
            count: doencas.length,
            color: 'text-red-600'
        },
        { 
            id: 'cirurgias', 
            label: 'Cirurgias', 
            icon: Activity,
            count: cirurgias.length,
            color: 'text-orange-600'
        },
        { 
            id: 'hospitalizacoes', 
            label: 'Hospitalizações', 
            icon: Hospital,
            count: hospitalizacoes.length,
            color: 'text-blue-600'
        },
        { 
            id: 'alergias', 
            label: 'Alergias', 
            icon: AlertTriangle,
            count: data?.alergias?.length || 0,
            color: 'text-yellow-600'
        },
        { 
            id: 'vacinas', 
            label: 'Vacinas', 
            icon: Syringe,
            count: data?.vacinas?.length || 0,
            color: 'text-green-600'
        },
        { 
            id: 'exames', 
            label: 'Exames', 
            icon: FileText,
            count: data?.exames?.length || 0,
            color: 'text-purple-600'
        },
        { 
            id: 'historico-familiar', 
            label: 'Histórico Familiar', 
            icon: Users,
            count: data?.historicoFamiliar?.length || 0,
            color: 'text-indigo-600'
        }
    ];

    const renderHistoricoMedicoTable = (items, tipo) => {
        if (!items || items.length === 0) {
            return (
                <div className="text-center py-8">
                    <p className="text-sm text-gray-500 mb-4">Nenhum registro encontrado.</p>
                    <PrimaryButton onClick={() => handleOpenModal(tipo)} className="flex items-center gap-2 mx-auto">
                        <Plus className="w-4 h-4" />
                        Adicionar {tipo === 'doenca' ? 'Doença' : tipo === 'cirurgia' ? 'Cirurgia' : 'Hospitalização'}
                    </PrimaryButton>
                </div>
            );
        }

        return (
            <>
                {/* Desktop */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                                {tipo === 'cirurgia' && (
                                    <>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Local</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Médico</th>
                                    </>
                                )}
                                {tipo === 'hospitalizacao' && (
                                    <>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Internação</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alta</th>
                                    </>
                                )}
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observações</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.map((item) => (
                                <tr key={item.id}>
                                    <td className="px-4 py-2 text-sm text-gray-700">{formatDate(item.data_ocorrencia)}</td>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.descricao}</td>
                                    {tipo === 'cirurgia' && (
                                        <>
                                            <td className="px-4 py-2 text-sm text-gray-700">{item.local_cirurgia || '-'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700">{item.medico_responsavel || '-'}</td>
                                        </>
                                    )}
                                    {tipo === 'hospitalizacao' && (
                                        <>
                                            <td className="px-4 py-2 text-sm text-gray-700">{formatDate(item.data_internacao)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-700">{formatDate(item.data_alta)}</td>
                                        </>
                                    )}
                                    <td className="px-4 py-2 text-sm text-gray-700">{item.observacoes || '-'}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="historico_medico"
                                            entidadeOrigemId={item.id}
                                            documentos={item.documentos}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        <DropdownMenu
                                            trigger={
                                                <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            }
                                            items={createActionDropdownItems(item, tipo, 'historico')}
                                            position="bottom-right"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile */}
                <div className="md:hidden space-y-3">
                    {items.map((item) => (
                        <div key={item.id} className="border rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-medium text-gray-900">{item.descricao}</div>
                                <DropdownMenu
                                    trigger={
                                        <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                            <MoreVertical className="w-4 h-4" />
                                        </button>
                                    }
                                    items={createActionDropdownItems(item, tipo, 'historico')}
                                    position="bottom-right"
                                />
                            </div>
                            <div className="text-sm text-gray-700 space-y-1">
                                <div><span className="font-medium">Data:</span> {formatDate(item.data_ocorrencia)}</div>
                                {tipo === 'cirurgia' && (
                                    <>
                                        {item.local_cirurgia && <div><span className="font-medium">Local:</span> {item.local_cirurgia}</div>}
                                        {item.medico_responsavel && <div><span className="font-medium">Médico:</span> {item.medico_responsavel}</div>}
                                    </>
                                )}
                                {tipo === 'hospitalizacao' && (
                                    <>
                                        {item.data_internacao && <div><span className="font-medium">Internação:</span> {formatDate(item.data_internacao)}</div>}
                                        {item.data_alta && <div><span className="font-medium">Alta:</span> {formatDate(item.data_alta)}</div>}
                                        {item.motivo_internacao && <div><span className="font-medium">Motivo:</span> {item.motivo_internacao}</div>}
                                    </>
                                )}
                                {item.observacoes && (
                                    <div className="mt-2 text-xs text-gray-600 italic">{item.observacoes}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const renderAlergiasTable = () => {
        const alergias = data?.alergias || [];
        if (alergias.length === 0) {
            return (
                <div className="text-center py-8">
                    <p className="text-sm text-gray-500 mb-4">Nenhuma alergia registrada.</p>
                    <PrimaryButton onClick={() => handleOpenModal('alergia')} className="flex items-center gap-2 mx-auto">
                        <Plus className="w-4 h-4" />
                        Adicionar Alergia
                    </PrimaryButton>
                </div>
            );
        }

        return (
            <>
                {/* Desktop */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severidade</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reação</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {alergias.map((alergia) => (
                                <tr key={alergia.id} className={alergia.severidade === 'grave' ? 'bg-red-50' : ''}>
                                    <td className="px-4 py-2 text-sm text-gray-700 capitalize">{alergia.tipo}</td>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{alergia.descricao}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <span className={`
                                            px-2 py-1 rounded-full text-xs font-medium
                                            ${alergia.severidade === 'grave' 
                                                ? 'bg-red-100 text-red-800' 
                                                : alergia.severidade === 'moderada'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }
                                        `}>
                                            {alergia.severidade || 'Não informada'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">{alergia.reacao || '-'}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="alergia"
                                            entidadeOrigemId={alergia.id}
                                            documentos={alergia.documentos}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        <DropdownMenu
                                            trigger={
                                                <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            }
                                            items={createActionDropdownItems(alergia, 'alergia', 'alergia')}
                                            position="bottom-right"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile */}
                <div className="md:hidden space-y-3">
                    {alergias.map((alergia) => (
                        <div 
                            key={alergia.id} 
                            className={`border rounded-lg p-3 ${alergia.severidade === 'grave' ? 'bg-red-50 border-red-200' : ''}`}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-medium text-gray-900">{alergia.descricao}</div>
                                <DropdownMenu
                                    trigger={
                                        <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                            <MoreVertical className="w-4 h-4" />
                                        </button>
                                    }
                                    items={createActionDropdownItems(alergia, 'alergia', 'alergia')}
                                    position="bottom-right"
                                />
                            </div>
                            <div className="text-sm text-gray-700 space-y-1">
                                <div><span className="font-medium">Tipo:</span> <span className="capitalize">{alergia.tipo}</span></div>
                                <div>
                                    <span className="font-medium">Severidade:</span>{' '}
                                    <span className={`
                                        px-2 py-1 rounded-full text-xs font-medium
                                        ${alergia.severidade === 'grave' 
                                            ? 'bg-red-100 text-red-800' 
                                            : alergia.severidade === 'moderada'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }
                                    `}>
                                        {alergia.severidade || 'Não informada'}
                                    </span>
                                </div>
                                {alergia.reacao && <div><span className="font-medium">Reação:</span> {alergia.reacao}</div>}
                                {alergia.tratamento && (
                                    <div className="mt-2 text-xs text-gray-600 italic">
                                        <span className="font-medium">Tratamento:</span> {alergia.tratamento}
                                    </div>
                                )}
                                <div className="mt-2">
                                    <span className="font-medium text-xs">Documentos:</span>
                                    <div className="mt-1">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="alergia"
                                            entidadeOrigemId={alergia.id}
                                            documentos={alergia.documentos}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const renderVacinasTable = () => {
        const vacinas = data?.vacinas || [];
        if (vacinas.length === 0) {
            return (
                <div className="text-center py-8">
                    <p className="text-sm text-gray-500 mb-4">Nenhuma vacina registrada.</p>
                    <PrimaryButton onClick={() => handleOpenModal('vacina')} className="flex items-center gap-2 mx-auto">
                        <Plus className="w-4 h-4" />
                        Adicionar Vacina
                    </PrimaryButton>
                </div>
            );
        }

        return (
            <>
                {/* Desktop */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vacina</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Aplicação</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dose</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Próxima Dose</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lote</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {vacinas.map((vacina) => (
                                <tr key={vacina.id}>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{vacina.nome_vacina}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700">{formatDate(vacina.data_aplicacao)}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700">
                                        {vacina.dose_numero ? `Dose ${vacina.dose_numero}` : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">
                                        {vacina.proxima_dose ? (
                                            <span className={new Date(vacina.proxima_dose) <= new Date() ? 'text-red-600 font-medium' : ''}>
                                                {formatDate(vacina.proxima_dose)}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">{vacina.lote || '-'}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="vacina"
                                            entidadeOrigemId={vacina.id}
                                            documentos={vacina.documentos}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        <DropdownMenu
                                            trigger={
                                                <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            }
                                            items={createActionDropdownItems(vacina, 'vacina', 'vacina')}
                                            position="bottom-right"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile */}
                <div className="md:hidden space-y-3">
                    {vacinas.map((vacina) => (
                        <div key={vacina.id} className="border rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-medium text-gray-900">{vacina.nome_vacina}</div>
                                <DropdownMenu
                                    trigger={
                                        <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                            <MoreVertical className="w-4 h-4" />
                                        </button>
                                    }
                                    items={createActionDropdownItems(vacina, 'vacina', 'vacina')}
                                    position="bottom-right"
                                />
                            </div>
                            <div className="text-sm text-gray-700 space-y-1">
                                <div><span className="font-medium">Data:</span> {formatDate(vacina.data_aplicacao)}</div>
                                {vacina.dose_numero && <div><span className="font-medium">Dose:</span> {vacina.dose_numero}</div>}
                                {vacina.proxima_dose && (
                                    <div>
                                        <span className="font-medium">Próxima Dose:</span>{' '}
                                        <span className={new Date(vacina.proxima_dose) <= new Date() ? 'text-red-600 font-medium' : ''}>
                                            {formatDate(vacina.proxima_dose)}
                                        </span>
                                    </div>
                                )}
                                {vacina.lote && <div><span className="font-medium">Lote:</span> {vacina.lote}</div>}
                                {vacina.profissional_aplicador && (
                                    <div><span className="font-medium">Aplicador:</span> {vacina.profissional_aplicador}</div>
                                )}
                                <div className="mt-2">
                                    <span className="font-medium text-xs">Documentos:</span>
                                    <div className="mt-1">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="vacina"
                                            entidadeOrigemId={vacina.id}
                                            documentos={vacina.documentos}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const renderExamesTable = () => {
        const exames = data?.exames || [];
        if (exames.length === 0) {
            return (
                <div className="text-center py-8">
                    <p className="text-sm text-gray-500 mb-4">Nenhum exame registrado.</p>
                    <PrimaryButton onClick={() => handleOpenModal('exame')} className="flex items-center gap-2 mx-auto">
                        <Plus className="w-4 h-4" />
                        Adicionar Exame
                    </PrimaryButton>
                </div>
            );
        }

        return (
            <>
                {/* Desktop */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Realização</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resultado</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {exames.map((exame) => (
                                <tr key={exame.id}>
                                    <td className="px-4 py-2 text-sm text-gray-700 capitalize">{exame.tipo_exame}</td>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{exame.nome_exame}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700">{formatDate(exame.data_realizacao)}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <span className={`
                                            px-2 py-1 rounded-full text-xs font-medium
                                            ${exame.status === 'realizado' 
                                                ? 'bg-green-100 text-green-800' 
                                                : exame.status === 'pendente'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }
                                        `}>
                                            {exame.status || 'Pendente'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">
                                        {exame.resultado ? (
                                            <span className="truncate block max-w-xs">{exame.resultado}</span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="exame"
                                            entidadeOrigemId={exame.id}
                                            documentos={exame.documentos}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        <DropdownMenu
                                            trigger={
                                                <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            }
                                            items={createActionDropdownItems(exame, 'exame', 'exame')}
                                            position="bottom-right"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile */}
                <div className="md:hidden space-y-3">
                    {exames.map((exame) => (
                        <div key={exame.id} className="border rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-medium text-gray-900">{exame.nome_exame}</div>
                                <DropdownMenu
                                    trigger={
                                        <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                            <MoreVertical className="w-4 h-4" />
                                        </button>
                                    }
                                    items={createActionDropdownItems(exame, 'exame', 'exame')}
                                    position="bottom-right"
                                />
                            </div>
                            <div className="text-sm text-gray-700 space-y-1">
                                <div><span className="font-medium">Tipo:</span> <span className="capitalize">{exame.tipo_exame}</span></div>
                                {exame.data_realizacao && <div><span className="font-medium">Data:</span> {formatDate(exame.data_realizacao)}</div>}
                                <div>
                                    <span className="font-medium">Status:</span>{' '}
                                    <span className={`
                                        px-2 py-1 rounded-full text-xs font-medium
                                        ${exame.status === 'realizado' 
                                            ? 'bg-green-100 text-green-800' 
                                            : exame.status === 'pendente'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }
                                    `}>
                                        {exame.status || 'Pendente'}
                                    </span>
                                </div>
                                {exame.resultado && (
                                    <div className="mt-2 text-xs text-gray-600">
                                        <span className="font-medium">Resultado:</span> {exame.resultado}
                                    </div>
                                )}
                                {exame.anexo_url && (
                                    <div className="mt-2">
                                        <a href={exame.anexo_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                                            Ver anexo
                                        </a>
                                    </div>
                                )}
                                <div className="mt-2">
                                    <span className="font-medium text-xs">Documentos:</span>
                                    <div className="mt-1">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="exame"
                                            entidadeOrigemId={exame.id}
                                            documentos={exame.documentos}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const renderHistoricoFamiliarTable = () => {
        const historicoFamiliar = data?.historicoFamiliar || [];
        if (historicoFamiliar.length === 0) {
            return (
                <div className="text-center py-8">
                    <p className="text-sm text-gray-500 mb-4">Nenhum histórico familiar registrado.</p>
                    <PrimaryButton onClick={() => handleOpenModal('historico-familiar')} className="flex items-center gap-2 mx-auto">
                        <Plus className="w-4 h-4" />
                        Adicionar Histórico Familiar
                    </PrimaryButton>
                </div>
            );
        }

        return (
            <>
                {/* Desktop */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parentesco</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condição Médica</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Idade Diagnóstico</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observações</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {historicoFamiliar.map((item) => (
                                <tr key={item.id}>
                                    <td className="px-4 py-2 text-sm text-gray-700">{item.parentesco}</td>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{item.condicao_medica}</td>
                                    <td className="px-4 py-2 text-sm text-gray-700">
                                        {item.idade_diagnostico ? `${item.idade_diagnostico} anos` : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">{item.observacoes || '-'}</td>
                                    <td className="px-4 py-2 text-sm">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="historico_familiar"
                                            entidadeOrigemId={item.id}
                                            documentos={item.documentos}
                                        />
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        <DropdownMenu
                                            trigger={
                                                <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            }
                                            items={createActionDropdownItems(item, 'historico-familiar', 'historico-familiar')}
                                            position="bottom-right"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile */}
                <div className="md:hidden space-y-3">
                    {historicoFamiliar.map((item) => (
                        <div key={item.id} className="border rounded-lg p-3">
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-medium text-gray-900">{item.parentesco}</div>
                                <DropdownMenu
                                    trigger={
                                        <button className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors">
                                            <MoreVertical className="w-4 h-4" />
                                        </button>
                                    }
                                    items={createActionDropdownItems(item, 'historico-familiar', 'historico-familiar')}
                                    position="bottom-right"
                                />
                            </div>
                            <div className="text-sm text-gray-700 space-y-1">
                                <div><span className="font-medium">Condição:</span> {item.condicao_medica}</div>
                                {item.idade_diagnostico && (
                                    <div><span className="font-medium">Idade no diagnóstico:</span> {item.idade_diagnostico} anos</div>
                                )}
                                {item.nome_familiar && (
                                    <div><span className="font-medium">Nome:</span> {item.nome_familiar}</div>
                                )}
                                {item.observacoes && (
                                    <div className="mt-2 text-xs text-gray-600 italic">{item.observacoes}</div>
                                )}
                                <div className="mt-2">
                                    <span className="font-medium text-xs">Documentos:</span>
                                    <div className="mt-1">
                                        <HistoricoMedicoDocumentos
                                            pacienteId={pacienteId}
                                            entidadeOrigem="historico_familiar"
                                            entidadeOrigemId={item.id}
                                            documentos={item.documentos}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    return (
        <div className="space-y-4">
            {isLoading && <LoadingIndicator message="Carregando histórico médico..." />}
            {error && <Alert type="error" message={error} />}

            {!isLoading && !error && (
                <>
                    {/* Navegação de Seções */}
                    <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
                        {sections.map((section) => {
                            const IconComponent = section.icon;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`
                                        px-4 py-2 text-sm font-medium transition-colors duration-200
                                        flex items-center gap-2 whitespace-nowrap
                                        ${activeSection === section.id
                                            ? 'border-b-2 border-emerald-500 text-emerald-600 font-bold'
                                            : 'text-gray-500 hover:text-emerald-600'
                                        }
                                    `}
                                >
                                    <IconComponent className={`w-4 h-4 ${activeSection === section.id ? section.color : ''}`} />
                                    {section.label} ({section.count})
                                </button>
                            );
                        })}
                    </div>

                    {/* Mensagem de Sucesso */}
                    {successMessage && (
                        <Alert type="success" message={successMessage} onClose={() => setSuccessMessage(null)} />
                    )}

                    {/* Seção: Doenças Prévia */}
                    {activeSection === 'doencas' && (
                        <Card>
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Doenças Prévia ({doencas.length} {doencas.length === 1 ? 'registro' : 'registros'})
                                </h3>
                                <PrimaryButton onClick={() => handleOpenModal('doenca')} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </PrimaryButton>
                            </div>
                            <div className="p-4">
                                {renderHistoricoMedicoTable(doencas, 'doenca')}
                            </div>
                        </Card>
                    )}

                    {/* Seção: Cirurgias */}
                    {activeSection === 'cirurgias' && (
                        <Card>
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Cirurgias ({cirurgias.length} {cirurgias.length === 1 ? 'registro' : 'registros'})
                                </h3>
                                <PrimaryButton onClick={() => handleOpenModal('cirurgia')} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </PrimaryButton>
                            </div>
                            <div className="p-4">
                                {renderHistoricoMedicoTable(cirurgias, 'cirurgia')}
                            </div>
                        </Card>
                    )}

                    {/* Seção: Hospitalizações */}
                    {activeSection === 'hospitalizacoes' && (
                        <Card>
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Hospitalizações ({hospitalizacoes.length} {hospitalizacoes.length === 1 ? 'registro' : 'registros'})
                                </h3>
                                <PrimaryButton onClick={() => handleOpenModal('hospitalizacao')} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </PrimaryButton>
                            </div>
                            <div className="p-4">
                                {renderHistoricoMedicoTable(hospitalizacoes, 'hospitalizacao')}
                            </div>
                        </Card>
                    )}

                    {/* Seção: Alergias */}
                    {activeSection === 'alergias' && (
                        <Card>
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Alergias ({data?.alergias?.length || 0} {data?.alergias?.length === 1 ? 'alergia' : 'alergias'})
                                </h3>
                                <PrimaryButton onClick={() => handleOpenModal('alergia')} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </PrimaryButton>
                            </div>
                            <div className="p-4">
                                {renderAlergiasTable()}
                            </div>
                        </Card>
                    )}

                    {/* Seção: Vacinas */}
                    {activeSection === 'vacinas' && (
                        <Card>
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Vacinas ({data?.vacinas?.length || 0} {data?.vacinas?.length === 1 ? 'vacina' : 'vacinas'})
                                </h3>
                                <PrimaryButton onClick={() => handleOpenModal('vacina')} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </PrimaryButton>
                            </div>
                            <div className="p-4">
                                {renderVacinasTable()}
                            </div>
                        </Card>
                    )}

                    {/* Seção: Exames */}
                    {activeSection === 'exames' && (
                        <Card>
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Exames ({data?.exames?.length || 0} {data?.exames?.length === 1 ? 'exame' : 'exames'})
                                </h3>
                                <PrimaryButton onClick={() => handleOpenModal('exame')} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </PrimaryButton>
                            </div>
                            <div className="p-4">
                                {renderExamesTable()}
                            </div>
                        </Card>
                    )}

                    {/* Seção: Histórico Familiar */}
                    {activeSection === 'historico-familiar' && (
                        <Card>
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Histórico Familiar ({data?.historicoFamiliar?.length || 0} {data?.historicoFamiliar?.length === 1 ? 'registro' : 'registros'})
                                </h3>
                                <PrimaryButton onClick={() => handleOpenModal('historico-familiar')} className="flex items-center gap-2">
                                    <Plus className="w-4 h-4" />
                                    Adicionar
                                </PrimaryButton>
                            </div>
                            <div className="p-4">
                                {renderHistoricoFamiliarTable()}
                            </div>
                        </Card>
                    )}

                    {/* Modais de Formulário */}
                    {isModalOpen && modalType && (
                        <>
                            {/* Modal para Histórico Médico (Doenças, Cirurgias, Hospitalizações) */}
                            {(modalType === 'doenca' || modalType === 'cirurgia' || modalType === 'hospitalizacao') && (
                                <Modal
                                    isOpen={isModalOpen}
                                    onClose={handleCloseModal}
                                    title={editingItem ? `Editar ${modalType === 'doenca' ? 'Doença' : modalType === 'cirurgia' ? 'Cirurgia' : 'Hospitalização'}` : `Adicionar ${modalType === 'doenca' ? 'Doença' : modalType === 'cirurgia' ? 'Cirurgia' : 'Hospitalização'}`}
                                    size="xl"
                                >
                                    <HistoricoMedicoForm
                                        onClose={handleCloseModal}
                                        onSubmit={handleSaveHistoricoMedico}
                                        initialData={editingItem || {}}
                                        tipo={modalType}
                                    />
                                </Modal>
                            )}

                            {/* Modal para Alergias */}
                            {modalType === 'alergia' && (
                                <Modal
                                    isOpen={isModalOpen}
                                    onClose={handleCloseModal}
                                    title={editingItem ? 'Editar Alergia' : 'Adicionar Alergia'}
                                    size="xl"
                                >
                                    <AlergiaForm
                                        onClose={handleCloseModal}
                                        onSubmit={handleSaveAlergia}
                                        initialData={editingItem || {}}
                                    />
                                </Modal>
                            )}

                            {/* Modal para Vacinas */}
                            {modalType === 'vacina' && (
                                <Modal
                                    isOpen={isModalOpen}
                                    onClose={handleCloseModal}
                                    title={editingItem ? 'Editar Vacina' : 'Adicionar Vacina'}
                                    size="xl"
                                >
                                    <VacinaForm
                                        onClose={handleCloseModal}
                                        onSubmit={handleSaveVacina}
                                        initialData={editingItem || {}}
                                    />
                                </Modal>
                            )}

                            {/* Modal para Exames */}
                            {modalType === 'exame' && (
                                <Modal
                                    isOpen={isModalOpen}
                                    onClose={handleCloseModal}
                                    title={editingItem ? 'Editar Exame' : 'Adicionar Exame'}
                                    size="xl"
                                >
                                    <ExameForm
                                        onClose={handleCloseModal}
                                        onSubmit={handleSaveExame}
                                        initialData={editingItem || {}}
                                    />
                                </Modal>
                            )}

                            {/* Modal para Histórico Familiar */}
                            {modalType === 'historico-familiar' && (
                                <Modal
                                    isOpen={isModalOpen}
                                    onClose={handleCloseModal}
                                    title={editingItem ? 'Editar Histórico Familiar' : 'Adicionar Histórico Familiar'}
                                    size="xl"
                                >
                                    <HistoricoFamiliarForm
                                        onClose={handleCloseModal}
                                        onSubmit={handleSaveHistoricoFamiliar}
                                        initialData={editingItem || {}}
                                    />
                                </Modal>
                            )}
                        </>
                    )}

                    {/* Modal de Confirmação de Exclusão */}
                    <ConfirmacaoModal
                        isOpen={isDeleteModalOpen}
                        onClose={() => {
                            setIsDeleteModalOpen(false);
                            setDeletingItem(null);
                            setDeleteType(null);
                        }}
                        onConfirm={handleConfirmDelete}
                        titulo="Confirmar Exclusão"
                        mensagem={`Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.`}
                        confirmText="Excluir"
                        cancelText="Cancelar"
                    />
                </>
            )}
        </div>
    );
};

export default PacienteHistoricoMedicoTab;

